import { Product } from "data/data";
import { getNestedModifierPrice } from "./modifierHelpers";

export interface TaxTotal {
  name: string;
  rate: number;
  amount: number;
}

/**
 * Calculate the total price for a single cart item including selected modifier upcharges,
 * multiplied by quantity.
 */
export const calculatingTotalPrice = (item: Product): number => {
  const { onlinePrice, quantity, modifierChecked, modifierSets } = item;
  let unitPrice = onlinePrice;

  if (modifierSets) {
    modifierSets.forEach((modifierSet) => {
      const matchingKey =
        modifierChecked &&
        Object.keys(modifierChecked)?.find(
          (key) => Number(key) === modifierSet.id
        );
      if (matchingKey) {
        modifierSet.modifierSetModifierItems?.forEach((modifierItem) => {
          const modifierItemId = modifierItem.id.toString();
          if (modifierChecked[matchingKey]?.[modifierItemId] !== undefined) {
            unitPrice += modifierChecked[matchingKey][modifierItemId]
              ? modifierItem.modifierItem.onlinePrice
              : 0;
          }
        });
      }
    });
  }

  unitPrice += getNestedModifierPrice(item);

  return quantity ? unitPrice * quantity : unitPrice;
};

/**
 * Calculate tax totals for a single cart item.
 * Returns an array of { name, rate, amount } for each tax applied.
 */
export const calculatingTotalTax = (item: Product): TaxTotal[] => {
  const { quantity, onlinePrice, taxes } = item;
  const baseQuantity = quantity ? 1 * quantity : onlinePrice;
  const taxTotals: TaxTotal[] = [];

  if (taxes) {
    taxes.forEach((tax) => {
      let totTaxAmt = 0;
      if (tax.type === "Percentage") {
        totTaxAmt = calculatingTotalPrice(item) * (tax.rate / 100);
      } else {
        totTaxAmt = baseQuantity * tax.rate;
      }
      taxTotals.push({ name: tax.name, rate: tax.rate, amount: totTaxAmt });
    });
  }

  return taxTotals;
};

/**
 * Get unique tax names from a single product's tax list.
 */
export const getTaxNames = (item: Product): string[] => {
  const { taxes } = item;
  const taxNames: string[] = [];
  if (taxes) {
    taxes.forEach((tax) => {
      taxNames.push(tax.name);
    });
  }
  return taxNames;
};

/**
 * Get tax details (rate + type) keyed by tax name from a single product.
 */
export const getTaxDetails = (
  item: Product
): Record<string, string> => {
  const { taxes } = item;
  const taxDetails: Record<string, string> = {};
  if (taxes) {
    taxes.forEach((tax) => {
      taxDetails[tax.name] = `${tax.rate},${tax.type}`;
    });
  }
  return taxDetails;
};

/**
 * Aggregate tax details across an array of products.
 */
export const aggregateTaxDetails = (
  products: Product[]
): Record<string, string> => {
  return products.reduce(
    (accumulator: Record<string, string>, product: Product) => {
      const details = getTaxDetails(product);
      Object.entries(details).forEach(([name, rateAndType]) => {
        if (!accumulator[name] || accumulator[name] !== rateAndType) {
          accumulator[name] = rateAndType;
        }
      });
      return accumulator;
    },
    {}
  );
};

/**
 * Build a display list of tax labels like "Tax Name: 8.25%"
 */
export const buildTaxDisplayList = (
  taxDetails: Record<string, string>
): string[] => {
  return Object.entries(taxDetails).map(([name, rateAndType]) => {
    const [rate, type] = (rateAndType as string).split(",");
    if (type === "Percentage") {
      return `${name}: ${rate}%`;
    } else if (type === "Fixed") {
      return `${name}: ${rate}`;
    }
    return `${name}: ${rate}%`;
  });
};

/**
 * Calculate totals for a list of products in the cart.
 * Returns subtotal, tax totals by name, total tax amount, and grand total.
 */
export const calculateCartTotals = (products: Product[]) => {
  const subtotal = products.reduce(
    (acc, item) => acc + calculatingTotalPrice(item),
    0
  );

  const allTaxTotals: TaxTotal[] = products.flatMap((product) =>
    calculatingTotalTax(product)
  );

  const taxTotalsByName = allTaxTotals.reduce(
    (acc: Record<string, number>, taxTotal) => {
      acc[taxTotal.name] = (acc[taxTotal.name] || 0) + taxTotal.amount;
      return acc;
    },
    {}
  );

  const taxDisplayTotals = Object.values(taxTotalsByName).map((amount) =>
    amount.toFixed(2)
  );

  const totalTax = Object.values(taxTotalsByName).reduce(
    (acc, amount) => acc + amount,
    0
  );

  const grandTotal = subtotal + totalTax;

  return {
    subtotal,
    taxTotalsByName,
    taxDisplayTotals,
    totalTax,
    grandTotal,
  };
};
